import React, { useState } from "react";
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  Switch, 
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Divider,
  Stack,
  IconButton as MuiIconButton
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";

/**
 * Custom IconButton component for the back button
 */
const IconButton = ({ children, onClick, sx }) => (
  <Box 
    component="button" 
    onClick={onClick} 
    sx={{ 
      cursor: 'pointer', 
      bgcolor: 'transparent', 
      border: 'none', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      p: 1,
      borderRadius: '50%',
      '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
      ...sx 
    }}
  >
    {children}
  </Box>
);

/**
 * Printer Layout Settings Component
 */
export const PrintLayoutSettings = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  
  // Mock list of existing layouts
  const [layouts, setLayouts] = useState([
    {
      id: "1",
      type: "sales",
      name: "Standard Sales",
      paperSize: "80",
      marginTop: 5,
      marginSides: 5,
      fontSizeStoreName: 14,
      boldStoreName: true,
      fontSizeAddress: 10,
      boldAddress: false,
      fontSizeDateTime: 10,
      boldDateTime: false,
      fontSizeOrderNo: 12,
      boldOrderNo: true,
      fontSizeItems: 11,
      boldItems: false,
      fontSizeTotals: 11,
      boldTotals: true,
      footerMsg: "Thank you for your business!",
      fontSizeFooter: 9,
      boldFooter: false,
    },
    {
      id: "2",
      type: "kitchen",
      name: "Kitchen Ticket",
      paperSize: "80",
      marginTop: 5,
      marginSides: 5,
      fontSizeDateTime: 12,
      boldDateTime: true,
      fontSizeOrderNo: 16,
      boldOrderNo: true,
      fontSizeItems: 14,
      boldItems: true,
      // Sales fields ignored for kitchen but kept in state for consistency
      fontSizeStoreName: 14, boldStoreName: false, fontSizeAddress: 10, boldAddress: false,
      fontSizeTotals: 11, boldTotals: false, footerMsg: "", fontSizeFooter: 9, boldFooter: false,
    }
  ]);

  const initialLayoutForm = {
    id: null,
    type: "sales",
    name: "",
    paperSize: "80",
    marginTop: 5,
    marginSides: 5,
    fontSizeStoreName: 14,
    boldStoreName: true,
    fontSizeAddress: 10,
    boldAddress: false,
    fontSizeDateTime: 10,
    boldDateTime: false,
    fontSizeOrderNo: 12,
    boldOrderNo: true,
    fontSizeItems: 11,
    boldItems: false,
    fontSizeTotals: 11,
    boldTotals: true,
    footerMsg: "Thank you for your business!",
    fontSizeFooter: 9,
    boldFooter: false,
  };

  const [layoutForm, setLayoutForm] = useState(initialLayoutForm);

  const handleOpen = (layout = null) => {
    if (layout) {
      setLayoutForm(layout);
    } else {
      setLayoutForm(initialLayoutForm);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    
    let processedValue;
    if (type === 'checkbox') {
      processedValue = checked;
    } else if (type === 'number') {
      processedValue = value === "" ? "" : parseFloat(value);
    } else {
      processedValue = value;
    }

    setLayoutForm(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleSave = () => {
    console.log("--- Saving Layout Configuration ---");
    console.log(JSON.parse(JSON.stringify(layoutForm)));
    
    if (layoutForm.id) {
      // Update existing
      setLayouts(prev => prev.map(l => l.id === layoutForm.id ? layoutForm : l));
    } else {
      // Add new
      setLayouts(prev => [...prev, { ...layoutForm, id: Date.now().toString() }]);
    }

    handleClose();
  };

  /**
   * Helper to render a control group for a specific item
   */
  const SettingItem = ({ label, sizeKey, boldKey }) => (
    <Box sx={{ mb: 2, p: 1.5, border: '1px solid #eee', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold">{label}</Typography>
      </Box>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={7}>
          <TextField
            fullWidth
            size="small"
            label="Font Size (px)"
            name={sizeKey}
            type="number"
            value={layoutForm[sizeKey]}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={5}>
          <FormControlLabel
            control={<Switch name={boldKey} checked={layoutForm[boldKey]} onChange={handleInputChange} size="small" />}
            label={<Typography variant="caption">Bold</Typography>}
          />
        </Grid>
      </Grid>
    </Box>
  );

  /**
   * Simple Live Preview Component
   */
  const ReceiptPreview = () => (
    <Paper 
      elevation={3} 
      sx={{ 
        pt: `${layoutForm.marginTop || 0}mm`, 
        px: `${layoutForm.marginSides || 0}mm`,
        width: '100%', 
        maxWidth: layoutForm.paperSize === "80" ? "300px" : "220px", 
        minHeight: '450px',
        bgcolor: '#fff',
        color: '#000',
        fontFamily: 'monospace',
        mx: 'auto',
        border: '1px solid #ddd',
        wordBreak: 'break-all',
        transition: 'max-width 0.3s ease-in-out'
      }}
    >
      {layoutForm.type === "sales" && (
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Typography sx={{ 
            fontWeight: layoutForm.boldStoreName ? 'bold' : 'normal',
            fontSize: `${layoutForm.fontSizeStoreName || 12}px`,
            lineHeight: 1.2
          }}>
            MIIRA POS STORE
          </Typography>
          <Typography sx={{ 
            fontSize: `${layoutForm.fontSizeAddress || 10}px`,
            fontWeight: layoutForm.boldAddress ? 'bold' : 'normal',
            lineHeight: 1.2
          }}>
            123 Business Avenue, Suite 100<br/>City, Country
          </Typography>
        </Box>
      )}

      {layoutForm.type === "sales" && <Divider sx={{ my: 1, borderStyle: 'dashed' }} />}

      <Box sx={{ mb: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          fontSize: `${layoutForm.fontSizeDateTime || 10}px`,
          fontWeight: layoutForm.boldDateTime ? 'bold' : 'normal'
        }}>
          <span>28/01/2026</span>
          <span>17:30</span>
        </Box>
        <Typography sx={{ 
          fontSize: `${layoutForm.fontSizeOrderNo || 10}px`,
          fontWeight: layoutForm.boldOrderNo ? 'bold' : 'normal'
        }}>
          Order #: 7742
        </Typography>
      </Box>

      <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

      <Box sx={{ 
        mb: 1, 
        fontSize: `${layoutForm.fontSizeItems || 10}px`,
        fontWeight: layoutForm.boldItems ? 'bold' : 'normal'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>2x Margherita Pizza</span>
          <span>{layoutForm.type === "sales" ? "$24.00" : ""}</span>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>1x Garlic Bread</span>
          <span>{layoutForm.type === "sales" ? "$6.50" : ""}</span>
        </Box>
      </Box>

      {layoutForm.type === "sales" && (
        <>
          <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
          <Box sx={{ 
            textAlign: 'right',
            fontSize: `${layoutForm.fontSizeTotals || 10}px`,
            fontWeight: layoutForm.boldTotals ? 'bold' : 'normal'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Subtotal:</span>
              <span>$39.50</span>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', mt: 0.5 }}>
              <span>TOTAL:</span>
              <span>$41.47</span>
            </Box>
          </Box>
          <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
          <Typography sx={{ 
            textAlign: 'center', 
            fontSize: `${layoutForm.fontSizeFooter || 10}px`,
            fontWeight: layoutForm.boldFooter ? 'bold' : 'normal'
          }}>
            {layoutForm.footerMsg}
          </Typography>
        </>
      )}
    </Paper>
  );

  return (
    <Box>
      <Container style={{ marginTop: '20px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton 
            onClick={() => navigate("/admin/settings?tab=2")}
            sx={{ border: '1px solid #222', mr: 2 }}
          >
            <ArrowBackIcon sx={{ color: '#222' }} />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">Printer Layout Settings</Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
          Configure your printer layouts here.
        </Typography>

        <Grid container spacing={3}>
          {layouts.map((layout) => (
            <Grid item xs={12} sm={6} md={3} key={layout.id}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  textAlign: 'center', 
                  cursor: 'pointer', 
                  position: 'relative',
                  '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' }
                }}
                onClick={() => handleOpen(layout)}
              >
                <Typography variant="subtitle1" fontWeight="bold">{layout.name}</Typography>
                <Typography variant="caption" display="block" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                  {layout.type} Layout ({layout.paperSize}mm)
                </Typography>
              </Paper>
            </Grid>
          ))}

          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '80px', 
                borderStyle: 'dashed',
                cursor: 'pointer',
                transition: '0.2s',
                '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' }
              }}
              onClick={() => handleOpen()}
            >
              <Typography variant="h4" color="primary">+</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Layout Configuration Popup with Live Preview */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {layoutForm.id ? `Edit ${layoutForm.name}` : "Create New Layout"}
          <Typography variant="caption" color="textSecondary">All sizes in px, margins in mm</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Settings Side */}
            <Grid item xs={12} md={7} sx={{ maxHeight: '70vh', overflowY: 'auto', pr: 1 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="primary">Global & Page Layout</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Receipt Type</InputLabel>
                    <Select name="type" value={layoutForm.type} label="Receipt Type" onChange={handleInputChange}>
                      <MenuItem value="sales">Sales Receipt</MenuItem>
                      <MenuItem value="kitchen">Kitchen Receipt</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Paper Size</InputLabel>
                    <Select name="paperSize" value={layoutForm.paperSize} label="Paper Size" onChange={handleInputChange}>
                      <MenuItem value="80">80mm (Standard)</MenuItem>
                      <MenuItem value="58">58mm (Mobile/Small)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Layout Name" name="name" value={layoutForm.name} onChange={handleInputChange} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Top Margin" name="marginTop" type="number" value={layoutForm.marginTop} onChange={handleInputChange} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Sides Margin" name="marginSides" type="number" value={layoutForm.marginSides} onChange={handleInputChange} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="primary">Element Customization</Typography>
              
              <Grid container spacing={1}>
                {layoutForm.type === "sales" ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <SettingItem label="Store Name" sizeKey="fontSizeStoreName" boldKey="boldStoreName" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SettingItem label="Store Address" sizeKey="fontSizeAddress" boldKey="boldAddress" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SettingItem label="Date & Time" sizeKey="fontSizeDateTime" boldKey="boldDateTime" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SettingItem label="Order Number" sizeKey="fontSizeOrderNo" boldKey="boldOrderNo" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SettingItem label="Items & Prices" sizeKey="fontSizeItems" boldKey="boldItems" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SettingItem label="Totals & Payment" sizeKey="fontSizeTotals" boldKey="boldTotals" />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <SettingItem label="Date & Time" sizeKey="fontSizeDateTime" boldKey="boldDateTime" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SettingItem label="Order Number" sizeKey="fontSizeOrderNo" boldKey="boldOrderNo" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SettingItem label="Items" sizeKey="fontSizeItems" boldKey="boldItems" />
                    </Grid>
                  </>
                )}
              </Grid>

              {layoutForm.type === "sales" && (
                <Box sx={{ mt: 2, p: 1.5, border: '1px solid #eee', borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Footer Message</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Message"
                    name="footerMsg"
                    multiline
                    rows={2}
                    value={layoutForm.footerMsg}
                    onChange={handleInputChange}
                    sx={{ mb: 2 }}
                  />
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={7}>
                      <TextField fullWidth size="small" label="Footer Font Size" name="fontSizeFooter" type="number" value={layoutForm.fontSizeFooter} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={5}>
                      <FormControlLabel
                        control={<Switch name="boldFooter" checked={layoutForm.boldFooter} onChange={handleInputChange} size="small" />}
                        label={<Typography variant="caption">Bold Footer</Typography>}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Grid>

            {/* Live Preview Side */}
            <Grid item xs={12} md={5} sx={{ bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', flexDirection: 'column', py: 2 }}>
              <Typography variant="caption" align="center" display="block" sx={{ mb: 1, fontWeight: 'bold', color: '#666' }}>
                LIVE PREVIEW ({layoutForm.type.toUpperCase()})
              </Typography>
              <Box sx={{ overflowY: 'auto', flexGrow: 1, px: 2 }}>
                <ReceiptPreview />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} color="inherit">Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">Save Layout</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrintLayoutSettings;